import { useTranslate, HttpError } from '@refinedev/core';
import { Edit, useForm } from '@refinedev/antd';
import { Form, Input, Radio, Tabs, Divider } from 'antd';
import type {
  ManagedCollectionDetailOut,
  ManagedCollectionUpdateIn,
} from '@/api/generated/schemas';
import { ModStatus, ManagedCollectionKind } from '@/api/generated/schemas';
import { CollectionImageFields } from '@/components/common/CollectionImageFields';
import {
  CollectionDataFields,
  normalizeCollectionData,
} from '@/components/query-builder';
import { LabelsField } from '@/components/common/LabelsField';
import { SearchPopularityField } from '@/components/common/SearchPopularityField';

export const ManagedCollectionEdit = () => {
  const t = useTranslate();
  const { formProps, saveButtonProps, query } = useForm<
    ManagedCollectionDetailOut,
    HttpError,
    ManagedCollectionUpdateIn
  >({
    resource: 'user_catalog.managed_collections',
    mutationMeta: { method: 'patch' },
  });
  const record = query?.data?.data;

  const handleFinish = (values: ManagedCollectionUpdateIn) =>
    formProps.onFinish?.(normalizeCollectionData(values));

  return (
    <Edit
      saveButtonProps={{
        ...saveButtonProps,
        disabled: saveButtonProps.disabled || record?.is_system,
      }}
      canDelete={!record?.is_system}
    >
      <Form
        {...formProps}
        onFinish={handleFinish}
        layout="vertical"
        disabled={record?.is_system}
      >
        <Tabs
          items={[
            {
              key: 'basic',
              label: t('base.sections.basic_info'),
              children: (
                <>
                  <Form.Item
                    name="name"
                    label={t('user_catalog.managed_collections.fields.name')}
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="kind"
                    label={t('user_catalog.managed_collections.fields.kind')}
                    rules={[{ required: true }]}
                  >
                    <Radio.Group
                      optionType="button"
                      buttonStyle="solid"
                      options={Object.values(ManagedCollectionKind).map(
                        (value) => ({
                          label: t(
                            `user_catalog.managed_collections.kind.${value}`
                          ),
                          value,
                          disabled: value === ManagedCollectionKind.personal,
                        })
                      )}
                    />
                  </Form.Item>

                  <Form.Item
                    name="description"
                    label={t(
                      'user_catalog.managed_collections.fields.description'
                    )}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>

                  <LabelsField />

                  <Divider>{t('base.sections.system_info')}</Divider>

                  <Form.Item
                    name="mod_status"
                    label={t('base.fields.mod_status')}
                    rules={[{ required: true }]}
                  >
                    <Radio.Group
                      optionType="button"
                      buttonStyle="solid"
                      options={Object.values(ModStatus).map((value) => ({
                        label: t(`base.mod_status.${value}`),
                        value,
                      }))}
                    />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'collection',
              label: t('base.sections.collection_info'),
              children: (
                <>
                  <Form.Item
                    name="header"
                    label={t('user_catalog.managed_collections.fields.header')}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="content"
                    label={t('user_catalog.managed_collections.fields.content')}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>

                  <CollectionImageFields
                    coverUrl={record?.cover_url}
                    bannerUrl={record?.banner_url}
                  />

                  <CollectionDataFields
                    mode="edit"
                    disabled={record?.is_system}
                  />
                </>
              ),
            },
            {
              key: 'search',
              label: t('user_catalog.common.tabs.search'),
              children: <SearchPopularityField />,
            },
          ]}
        />
      </Form>
    </Edit>
  );
};
